import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService, type DashboardStats } from '../services/dashboardService';

const DashboardPage: React.FC = () => {
  const { user, hasScope } = useAuth();
  const isDeveloper = hasScope('dev');
  const isAdmin = hasScope('admin');

  const [stats, setStats] = useState<DashboardStats | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [statsResponse] = await Promise.all([
        dashboardService.getDashboardStats(),
        dashboardService.getRecentActivity(5)
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      } else {
        setError(statsResponse.message || 'Failed to load dashboard statistics');
      }


    } catch (error: any) {
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.fullName}!
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Role: {isDeveloper ? 'Developer' : isAdmin ? 'Administrator' : 'User'}
          </p>
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-red-400">⚠️</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                    onClick={loadDashboardData}
                    className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-medium">👤</span>
                  </div>
                </div>
                <div className="ml-3 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                      {isDeveloper ? 'Total Users' : 'My Profiles'}
                    </dt>
                    <dd className="text-sm sm:text-lg font-medium text-gray-900">
                      {isDeveloper
                        ? dashboardService.formatNumber(stats?.totalUsers || 0)
                        : dashboardService.formatNumber(stats?.myProfiles || 0)
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-medium">✓</span>
                  </div>
                </div>
                <div className="ml-3 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                      {isDeveloper ? 'Total Profiles' : 'Active Profiles'}
                    </dt>
                    <dd className="text-sm sm:text-lg font-medium text-gray-900">
                      {isDeveloper
                        ? dashboardService.formatNumber(stats?.totalProfiles || 0)
                        : dashboardService.formatNumber(stats?.activeProfiles || 0)
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-medium">💳</span>
                  </div>
                </div>
                <div className="ml-3 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                      {isDeveloper ? 'Total Transactions' : 'My Transactions'}
                    </dt>
                    <dd className="text-sm sm:text-lg font-medium text-gray-900">
                      {isDeveloper
                        ? dashboardService.formatNumber(stats?.totalTransactions || 0)
                        : dashboardService.formatNumber(stats?.myTransactions || 0)
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-xs sm:text-sm font-medium">📊</span>
                  </div>
                </div>
                <div className="ml-3 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                      {isDeveloper ? 'Total Volume' : 'Success Rate'}
                    </dt>
                    <dd className="text-sm sm:text-lg font-medium text-gray-900">
                      {isDeveloper
                        ? dashboardService.formatCurrency(stats?.totalVolume || 0, 'MYR')
                        : (stats?.successRate || '0.0%')
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {isDeveloper ? (
              <>
                <button className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg p-3 sm:p-4 text-left transition-colors">
                  <div className="text-xs sm:text-sm font-medium">Manage Users</div>
                  <div className="text-xs text-blue-200 mt-1 hidden sm:block">
                    Create and manage user accounts
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/profiles'}
                  className="bg-gray-100 text-gray-900 hover:bg-gray-200 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">View All Profiles</div>
                  <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                    Monitor all system profiles
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/settings'}
                  className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">System Analytics</div>
                  <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                    View system-wide reports
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/transactions'}
                  className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">Global Transactions</div>
                  <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                    Monitor all transactions
                  </div>
                </button>
              </>
            ) : (
              <>
                <button 
                  onClick={() => window.location.href = '/qr-payment'}
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">Create QR Payment</div>
                  <div className="text-xs text-blue-200 mt-1 hidden sm:block">
                    Generate payment QR code
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/profiles'}
                  className="bg-gray-100 text-gray-900 hover:bg-gray-200 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">My Profiles</div>
                  <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                    Manage your profiles
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/transactions'}
                  className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">Transaction History</div>
                  <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                    View your transactions
                  </div>
                </button>
                <button 
                  onClick={() => window.location.href = '/settings'}
                  className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 rounded-lg p-3 sm:p-4 text-left transition-colors"
                >
                  <div className="text-xs sm:text-sm font-medium">Settings</div>
                  <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                    Account settings
                  </div>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 sm:p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base sm:text-lg font-medium text-gray-900">Recent Transactions</h2>
            <button 
              onClick={() => window.location.href = '/transactions'}
              className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-medium"
            >
              View all
            </button>
          </div>
          
          <div className="text-center py-6 sm:py-8">
            <div className="text-gray-400 text-base sm:text-lg">No recent transactions</div>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">Create your first transaction to get started</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
